import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@components/ui/button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/shadcn/dialog';
import { Input } from '@components/ui/shadcn/input';
import { Label } from '@components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/shadcn/select';
import { TextArea } from '@components/ui/textarea/TextArea';
import { faFileExcel, faUpload } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryModel } from '../../models/Country';
import { PaymentMethodModel } from '../../models/PaymentMethod';

interface BulkImportCoverageRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    file: File;
    countryId: number;
    typeRule: 'allow' | 'deny';
    paymentMethodId?: number | null;
    notes?: string | null;
    groupRanges: boolean;
  }) => void;
  isSubmitting?: boolean;
  paymentMethods: PaymentMethodModel[];
  countries: CountryModel[];
}

export const BulkImportCoverageRuleModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  paymentMethods,
  countries,
}: BulkImportCoverageRuleModalProps) => {
  const { t } = useTranslation('coverageZonesModule');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [file, setFile] = useState<File | null>(null);
  const [countryId, setCountryId] = useState<string>('');
  const [ruleType, setRuleType] = useState<'allow' | 'deny'>('allow');
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [groupRanges, setGroupRanges] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setCountryId('');
      setRuleType('allow');
      setPaymentMethodId('');
      setNotes('');
      setGroupRanges(true);
      setErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!file) {
      newErrors.file = t('validation.fileRequired');
    } else {
      // Validar tipo de archivo
      const allowedExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
      ];

      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        newErrors.file = t('validation.fileTypeInvalid');
      }

      // Validar tamaño máximo (10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB en bytes
      if (file.size > maxSize) {
        newErrors.file = t('validation.fileSizeExceeded');
      }
    }

    if (!countryId) {
      newErrors.country_id = t('validation.countryRequired');
    }

    // Validar longitud de notas (máximo 1000 caracteres)
    if (notes && notes.length > 1000) {
      newErrors.notes = t('validation.notesMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Limpiar error de archivo si existe
      if (errors.file) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.file;
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate() || !file) return;

    onSubmit({
      file,
      countryId: parseInt(countryId),
      typeRule: ruleType,
      paymentMethodId: paymentMethodId ? parseInt(paymentMethodId) : null,
      notes: notes.trim() || null,
      groupRanges,
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto transition-colors">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('bulkImport.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Importa múltiples códigos postales desde un archivo Excel para crear reglas de cobertura masivamente
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Archivo Excel */}
          <div className="space-y-2">
            <Label htmlFor="file" className="text-foreground">
              {t('bulkImport.file')} <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
                className="hidden"
                disabled={isSubmitting}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleFileButtonClick}
                disabled={isSubmitting}
                className="w-full transition-colors"
              >
                <FontAwesomeIcon icon={faUpload} className="mr-2 h-4 w-4" />
                {t('bulkImport.selectFile')}
              </Button>
              {file && (
                <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border border-border">
                  <FontAwesomeIcon icon={faFileExcel} className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-foreground truncate flex-1">{file.name}</span>
                  <span className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              )}
            </div>
            {errors.file && <p className="text-sm text-destructive">{errors.file}</p>}
          </div>

          {/* País */}
          <div className="space-y-2">
            <Label htmlFor="country_id" className="text-foreground">
              {t('form.country')} <span className="text-destructive">*</span>
            </Label>
            <Combobox
              options={countries.map((c) => ({
                label: c.name,
                value: String(c.id),
                searchValue: `${c.name} ${c.iso2 || ''} ${c.iso3 || ''}`.trim(),
              }))}
              placeholder={t('form.selectCountry')}
              value={countryId}
              setValue={setCountryId}
              disabled={isSubmitting}
            />
            {errors.country_id && <p className="text-sm text-destructive">{errors.country_id}</p>}
          </div>

          {/* Tipo de Regla */}
          <div className="space-y-2">
            <Label htmlFor="rule_type" className="text-foreground">
              {t('form.type')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={ruleType}
              onValueChange={(value) => setRuleType(value as 'allow' | 'deny')}
              disabled={isSubmitting}
            >
              <SelectTrigger id="rule_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">{t('form.typeAllow')}</SelectItem>
                <SelectItem value="deny">{t('form.typeDeny')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Método de Pago */}
          <div className="space-y-2">
            <Label htmlFor="payment_method" className="text-foreground">
              {t('form.paymentMethod')} <span className="text-muted-foreground text-xs">({t('form.optional')})</span>
            </Label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Select value={paymentMethodId} onValueChange={setPaymentMethodId} disabled={isSubmitting}>
                  <SelectTrigger id="payment_method">
                    <SelectValue placeholder={t('form.selectPaymentMethod')} />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.id.toString()}>
                        {method.method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {paymentMethodId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setPaymentMethodId('');
                  }}
                  disabled={isSubmitting}
                  className="px-3 shrink-0"
                >
                  Limpiar
                </Button>
              )}
            </div>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-foreground">
              {t('form.notes')} <span className="text-muted-foreground text-xs">({t('form.optional')})</span>
            </Label>
            <TextArea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Información adicional sobre esta importación..."
              rows={3}
              disabled={isSubmitting}
              className={`resize-none ${errors.notes ? 'border-destructive' : ''}`}
              maxLength={1000}
            />
            <div className="flex justify-between items-center">
              {errors.notes && <p className="text-sm text-destructive">{errors.notes}</p>}
              <span className="text-xs text-muted-foreground ml-auto">{notes.length}/1000</span>
            </div>
          </div>

          {/* Group Ranges */}
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="group_ranges"
                checked={groupRanges}
                onChange={(e) => setGroupRanges(e.target.checked)}
                disabled={isSubmitting}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-ring focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <div className="flex-1">
                <Label htmlFor="group_ranges" className="text-foreground font-medium cursor-pointer">
                  {t('bulkImport.groupRanges')}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">{t('bulkImport.groupRangesDescription')}</p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="transition-colors"
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} className="transition-colors">
              {isSubmitting ? t('bulkImport.importing') : t('bulkImport.import')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
